import { Field, FieldDescriptionOrError, FieldLabel, cn } from "@motley/design"
import { Suspense, lazy, useState } from "react"
import { jsonFieldMetaSchema } from "./jsonFieldMetaSchema"
import { jsonFieldCompletion } from "./schemaCompletion"
import {
  isJsonField,
  normalizeJsonFieldShape,
  stringifySchema,
  validateJsonField,
} from "./utils"
import type { JsonField } from "./model"
import { CodeEditor } from "@/script"

const JsonFieldEditor = lazy(() => import("./JsonFieldEditor"))

/**
 * Normalises before validating so the lint markers agree with `handleCodeChange`:
 * an edit is flagged if and only if it will be rejected.
 */
const validateCode = (parsed: unknown) => {
  normalizeJsonFieldShape(parsed)

  return validateJsonField(parsed)
}

export type Props = {
  value: JsonField
  onChange: (schema: JsonField) => void
  label?: string
  error?: string
  helperText?: string
  className?: string
}

export default function JsonSchemaField({
  value,
  onChange,
  label,
  error,
  helperText,
  className = "",
}: Props) {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )

  const handleFieldChange = (nextValue: JsonField) => {
    setErrorMessage(undefined)
    onChange(nextValue)
  }

  const handleCodeChange = (nextCode: string) => {
    try {
      const parsed: unknown = JSON.parse(nextCode)

      normalizeJsonFieldShape(parsed)

      if (isJsonField(parsed)) {
        setErrorMessage(undefined)
        onChange(parsed)
      }
    } catch {
      setErrorMessage("Invalid JSON")
    }
  }

  return (
    <Field className={cn("flex flex-col", className)} aria-invalid={!!error}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <div className="flex min-h-0 flex-1 flex-row gap-4">
        <div className="min-w-1/4 flex-1 basis-1/2 overflow-y-auto">
          <Suspense>
            <JsonFieldEditor
              value={value}
              onChange={handleFieldChange}
              isRoot
            />
          </Suspense>
        </div>
        <div className="flex min-w-1/4 flex-1 basis-1/2 flex-col">
          <CodeEditor
            language="json"
            code={stringifySchema(value)}
            onChange={handleCodeChange}
            error={errorMessage}
            jsonSchema={jsonFieldMetaSchema}
            validate={validateCode}
            complete={jsonFieldCompletion}
            fillHeight
          />
        </div>
      </div>
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
